import { useEffect, useState } from "react";
import { Check, Eye, Loader, Mail, Send } from "lucide-react";
import { apiFetch } from "./api";

/**
 * Client Emails — the practitioner's half of what lands in their client's inbox.
 *
 * They write the message and tick the reminders. The receipt, the cancellation terms and
 * the independent-practitioner footer are platform-written and not editable here, on
 * purpose: a disclosure a practitioner can delete is not a disclosure (owner, Jun 2026).
 */
export default function ClientEmailsPanel({ profile, onSaved }) {
  const [options, setOptions]   = useState(null);
  const [stage, setStage]       = useState("confirmed");
  const [settings, setSettings] = useState({
    business_name: "", contact_email: "", presets: [], messages: {},
  });
  const [saving, setSaving]     = useState(false);
  const [saved, setSaved]       = useState(false);
  const [preview, setPreview]   = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending]   = useState(false);
  const [sentTo, setSentTo]     = useState(null);
  const [error, setError]       = useState("");

  useEffect(() => {
    apiFetch("/practitioner/client-emails/options").then(setOptions).catch(() => {});
  }, []);

  useEffect(() => {
    const ce = profile?.client_emails;
    if (ce) {
      setSettings({
        business_name: ce.business_name || "",
        contact_email: ce.contact_email || "",
        presets:       ce.presets || [],
        messages:      ce.messages || {},
      });
    }
  }, [profile]);

  const setMessage = (text) =>
    setSettings(s => ({ ...s, messages: { ...s.messages, [stage]: text } }));

  const togglePreset = (id) =>
    setSettings(s => ({
      ...s,
      presets: s.presets.includes(id) ? s.presets.filter(p => p !== id) : [...s.presets, id],
    }));

  const save = async () => {
    setSaving(true); setError("");
    try {
      const updated = await apiFetch("/practitioner/booking-profile", {
        method: "PUT",
        body: JSON.stringify({ client_emails: settings }),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      onSaved?.(updated);
    } catch (e) {
      setError(e.message || "We couldn't save that.");
    } finally {
      setSaving(false);
    }
  };

  const loadPreview = async () => {
    setPreviewing(true); setError("");
    try {
      // Saved first, so the preview is genuinely what a client would receive rather than
      // a render of unsaved text.
      await apiFetch("/practitioner/booking-profile", {
        method: "PUT", body: JSON.stringify({ client_emails: settings }),
      });
      setPreview(await apiFetch(`/practitioner/client-emails/preview?stage=${stage}`));
    } catch (e) {
      setError(e.message || "Preview failed.");
    } finally {
      setPreviewing(false);
    }
  };

  const sendTest = async () => {
    setSending(true); setError(""); setSentTo(null);
    try {
      await apiFetch("/practitioner/booking-profile", {
        method: "PUT", body: JSON.stringify({ client_emails: settings }),
      });
      const r = await apiFetch(`/practitioner/client-emails/test-send?stage=${stage}`, { method: "POST" });
      setSentTo(r.recipient);
    } catch (e) {
      setError(e.message || "We couldn't send that.");
    } finally {
      setSending(false);
    }
  };

  if (!options) {
    return (
      <div className="vav-section" data-testid="client-emails-loading">
        <Loader size={16} style={{ animation: "spin 1s linear infinite" }} />
      </div>
    );
  }

  const stagePresets = options.presets.filter(p => p.stages.includes(stage));
  const message = settings.messages[stage] || "";
  const overLimit = message.length > options.max_length;

  return (
    <div data-testid="client-emails-panel">
      <div className="vav-section">
        <p className="vav-section-title">
          <Mail size={14} style={{ verticalAlign: -2, marginRight: 6 }} />
          How you sound to your clients
        </p>
        <p className="vav-section-desc">
          Your clients hear from you, not from us. Write the part that sounds like you and tick
          the reminders you want included. Every email also carries the receipt, your
          cancellation terms and a line explaining that you're an independent practitioner —
          those are written by the platform and can't be edited, so your clients always get the
          same straight answer about their money.
        </p>

        <div className="vav-session-grid" style={{ marginTop: 14 }}>
          <div className="vav-session-field">
            <label className="vav-session-label">Your business name (optional)</label>
            <p style={{ fontSize: 11, color: "var(--ink-light)", margin: "0 0 4px" }}>
              Used in the signature and the independent-practitioner line. Blank uses your
              profile name.
            </p>
            <input
              className="vav-session-input"
              style={{ width: "100%" }}
              placeholder="e.g. Buelow Wellness"
              value={settings.business_name}
              onChange={e => setSettings(s => ({ ...s, business_name: e.target.value }))}
              data-testid="client-emails-business-name"
            />
          </div>
          <div className="vav-session-field">
            <label className="vav-session-label">Where clients should reply (optional)</label>
            <p style={{ fontSize: 11, color: "var(--ink-light)", margin: "0 0 4px" }}>
              Shown as "Questions? Email…" at the bottom of each email.
            </p>
            <input
              className="vav-session-input"
              style={{ width: "100%" }}
              placeholder="you@yourpractice.com"
              value={settings.contact_email}
              onChange={e => setSettings(s => ({ ...s, contact_email: e.target.value }))}
              data-testid="client-emails-contact-email"
            />
          </div>
        </div>
      </div>

      <div className="vav-section">
        <p className="vav-section-title">Write each email</p>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
          {options.stages.map(s => (
            <button
              key={s.id}
              onClick={() => { setStage(s.id); setPreview(null); setSentTo(null); }}
              data-testid={`client-emails-stage-${s.id}`}
              style={{
                padding: "7px 14px", borderRadius: 999, fontSize: 12, cursor: "pointer",
                fontFamily: "'Source Sans 3', sans-serif",
                border: `1px solid ${stage === s.id ? "var(--teal)" : "var(--border)"}`,
                background: stage === s.id ? "var(--teal-bg)" : "transparent",
                color: stage === s.id ? "var(--teal)" : "var(--ink-mid)",
                fontWeight: stage === s.id ? 600 : 400,
              }}
            >
              {s.label}
              {(settings.messages[s.id] || "").trim() && " ·"}
            </button>
          ))}
        </div>

        <div className="vav-session-field">
        <label className="vav-session-label">Your message</label>
        <textarea
          className="vav-session-input"
          style={{ width: "100%", minHeight: 140, resize: "vertical", lineHeight: 1.6, fontFamily: "'Source Sans 3', sans-serif" }}
          placeholder={
            stage === "requested"
              ? "e.g. Thank you for reaching out. I'll confirm within a day, and I'm looking forward to working with you."
              : stage === "confirmed"
              ? "e.g. I'm so glad we're meeting. Come as you are — there's nothing you need to prepare."
              : stage === "cancelled"
              ? "e.g. No trouble at all. Reach out whenever the time feels right."
              : "e.g. Thank you for today. Be gentle with yourself this evening."
          }
          value={message}
          onChange={e => setMessage(e.target.value)}
          data-testid={`client-emails-message-${stage}`}
        />
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
          <span style={{ fontSize: 11, color: "var(--ink-light)" }}>
            Leave blank to send no message of your own at this stage.
          </span>
          <span style={{ fontSize: 11, color: overLimit ? "#b4472f" : "var(--ink-light)" }}
                data-testid="client-emails-char-count">
            {message.length} / {options.max_length}
          </span>
        </div>

        <div style={{ marginTop: 10 }}>
          <p style={{ fontSize: 11, color: "var(--ink-light)", marginBottom: 6 }}>
            Drop these in and they fill themselves in for each client:
          </p>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {options.merge_tags.map(t => (
              <button
                key={t.tag}
                title={t.description}
                onClick={() => setMessage(`${message}${message && !message.endsWith(" ") ? " " : ""}${t.tag}`)}
                data-testid={`client-emails-tag-${t.tag.replace(/[{}]/g, "")}`}
                style={{
                  padding: "4px 9px", borderRadius: 6, fontSize: 11, cursor: "pointer",
                  border: "1px dashed var(--border)", background: "transparent",
                  color: "var(--ink-mid)", fontFamily: "monospace",
                }}
              >
                {t.tag}
              </button>
            ))}
          </div>
        </div>

        {stagePresets.length > 0 && (
          <div style={{ marginTop: 18 }}>
            <label className="vav-session-label" style={{ display: "block" }}>Reminders to include</label>
            <p style={{ fontSize: 11, color: "var(--ink-light)", margin: "0 0 8px" }}>
              We wrote these so they read consistently. Tick what applies to your work.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {stagePresets.map(p => (
                <label
                  key={p.id}
                  style={{
                    display: "flex", gap: 9, alignItems: "flex-start", cursor: "pointer",
                    fontSize: 13, color: "var(--ink-mid)", lineHeight: 1.5,
                  }}
                  data-testid={`client-emails-preset-${p.id}`}
                >
                  <input
                    type="checkbox"
                    checked={settings.presets.includes(p.id)}
                    onChange={() => togglePreset(p.id)}
                    style={{ marginTop: 3 }}
                  />
                  <span>
                    <strong style={{ fontWeight: 600 }}>{p.label}</strong>
                    <span style={{ display: "block", fontSize: 12, color: "var(--ink-light)" }}>
                      "{p.text}"
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}

        {stage === "confirmed" && (
          <p style={{
            fontSize: 12, color: "var(--ink-light)", marginTop: 14, padding: "10px 12px",
            background: "var(--surface)", borderRadius: 8, lineHeight: 1.5,
          }}>
            Any session type with its own "Introduction email message" filled in uses that
            instead of this, for that offering only.
          </p>
        )}

        {error && (
          <p style={{ fontSize: 12, color: "#b4472f", marginTop: 12 }} data-testid="client-emails-error">
            {error}
          </p>
        )}

        <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 18, flexWrap: "wrap" }}>
          <button
            className="vav-save-btn"
            onClick={save}
            disabled={saving || overLimit}
            data-testid="client-emails-save-btn"
          >
            {saving
              ? <Loader size={14} style={{ animation: "spin 1s linear infinite" }} />
              : <Check size={14} />}
            Save
          </button>
          <button
            className="vav-save-btn" style={{ background: "transparent", color: "var(--teal)", border: "1px solid var(--teal)" }}
            onClick={loadPreview}
            disabled={previewing}
            data-testid="client-emails-preview-btn"
          >
            {previewing
              ? <Loader size={14} style={{ animation: "spin 1s linear infinite" }} />
              : <Eye size={14} />}
            See what they get
          </button>
          <button
            className="vav-save-btn" style={{ background: "transparent", color: "var(--teal)", border: "1px solid var(--teal)" }}
            onClick={sendTest}
            disabled={sending}
            data-testid="client-emails-test-send-btn"
          >
            {sending
              ? <Loader size={14} style={{ animation: "spin 1s linear infinite" }} />
              : <Send size={14} />}
            Send it to me
          </button>
          {saved && (
            <span style={{ fontSize: 12, color: "var(--teal)" }} data-testid="client-emails-saved">
              Saved
            </span>
          )}
          {sentTo && (
            <span style={{ fontSize: 12, color: "var(--teal)" }} data-testid="client-emails-sent">
              Sent to {sentTo}
            </span>
          )}
        </div>
      </div>

      {preview && (
        <div className="vav-section" data-testid="client-emails-preview">
          <p className="vav-section-title">Preview — {preview.subject}</p>
          <p className="vav-section-desc">
            Sample details, your wording. The receipt and terms below your message are the
            platform's and appear whatever you write.
          </p>
          <iframe
            title="Email preview"
            srcDoc={preview.html}
            style={{
              width: "100%", height: 620, border: "1px solid var(--border)",
              borderRadius: 10, background: "#fff", marginTop: 12,
            }}
          />
        </div>
      )}
    </div>
  );
}
